import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { formatCurrency } from 'utils/formatCLP';

export default function ProductPreview({
  nombre,
  precio,
  descripcion,
  activo,
  featured,
  onSale,
  images,
  mainImageOverrideUrl = null,
  currencyCode = 'USD',
  locale = 'en-US',
  isRestaurant = false,
}) {
  const imageList = images?.length ? images : [];
  const [activeIdx, setActiveIdx] = useState(0);
  const safeIdx = Math.min(activeIdx, Math.max(0, imageList.length - 1));
  const mainImage = imageList[safeIdx];
  const mainImageUrl = safeIdx === 0 && mainImageOverrideUrl ? mainImageOverrideUrl : mainImage?.url;
  const priceNum = Number(precio);
  const formattedPrice = Number.isFinite(priceNum) && priceNum > 0
    ? formatCurrency(priceNum, currencyCode, locale)
    : null;

  const goPrev = () => setActiveIdx(i => (i <= 0 ? imageList.length - 1 : i - 1));
  const goNext = () => setActiveIdx(i => (i >= imageList.length - 1 ? 0 : i + 1));

  React.useEffect(() => {
    setActiveIdx(0);
  }, [imageList.length]);

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <div
          className="flex h-8 w-8 items-center justify-center rounded-xl"
          style={{ backgroundColor: 'rgba(17,24,39,0.06)' }}
        >
          <Icon name="Smartphone" size={14} color="var(--color-foreground)" />
        </div>
        <h3 className="text-base font-semibold" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)', letterSpacing: '-0.015em' }}>
          Vista previa
        </h3>
        <span
          className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium"
          style={{
            backgroundColor: activo ? 'rgba(5,150,105,0.1)' : 'rgba(107,107,107,0.1)',
            color: activo ? '#059669' : 'var(--color-muted-foreground)',
            fontFamily: 'var(--font-caption)',
          }}
        >
          {activo ? '● Visible' : '○ Oculto'}
        </span>
      </div>

      {/* Phone mockup */}
      <div className="mx-auto" style={{ maxWidth: '252px' }}>
        <div
          className="overflow-hidden rounded-[28px] border"
          style={{
            borderColor: 'rgba(17,24,39,0.10)',
            backgroundColor: '#f8f8f8',
            boxShadow: '0 18px 42px rgba(17,24,39,0.14), 0 0 0 1px rgba(255,255,255,0.70)',
          }}
        >
          {/* Phone notch */}
          <div
            className="flex items-center justify-center py-2"
            style={{ backgroundColor: '#1a1a1a' }}
          >
            <div className="w-14 h-1 rounded-full" style={{ backgroundColor: '#333' }} />
          </div>

          {/* Status bar */}
          <div
            className="flex items-center justify-between px-3 py-1"
            style={{ backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0' }}
          >
            <span style={{ fontSize: '0.6rem', color: '#666', fontFamily: 'var(--font-caption)' }}>{isRestaurant ? 'Mi restaurante' : 'Mi tienda'}</span>
            <div className="flex items-center gap-1">
              <Icon name="Wifi" size={9} color="#666" />
              <Icon name="Battery" size={9} color="#666" />
            </div>
          </div>

          {/* Product image */}
          <div className="relative aspect-square w-full overflow-hidden" style={{ backgroundColor: '#f0f0f0' }}>
            {mainImageUrl ? (
              <Image key={`img-${safeIdx}-${mainImageUrl}`} src={mainImageUrl} alt={mainImage?.alt || nombre} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                <Icon name="ImageOff" size={28} color="#ccc" />
                <span style={{ fontSize: '0.65rem', color: '#aaa', fontFamily: 'var(--font-caption)' }}>Sin imagen</span>
              </div>
            )}

            {/* Flechas anterior/siguiente */}
            {imageList.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  aria-label="Imagen anterior"
                >
                  <Icon name="ChevronLeft" size={14} color="#fff" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                  aria-label="Siguiente imagen"
                >
                  <Icon name="ChevronRight" size={14} color="#fff" />
                </button>
              </>
            )}

            {/* Un badge como en el catálogo: Oferta > Más vendido */}
            {(onSale || featured) && (
              <div
                className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: onSale ? '#dc2626' : '#b45309',
                  boxShadow: onSale ? '0 2px 8px rgba(220,38,38,0.35)' : '0 2px 8px rgba(180,83,9,0.35)',
                }}
              >
                <Icon name={onSale ? 'Tag' : 'Star'} size={9} color="#fff" />
                <span style={{ fontSize: '0.6rem', color: '#fff', fontFamily: 'var(--font-caption)', fontWeight: 600 }}>
                  {onSale ? 'Oferta' : 'Más vendido'}
                </span>
              </div>
            )}

            {/* Image counter */}
            {imageList.length > 1 && (
              <div
                className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
              >
                <span style={{ fontSize: '0.6rem', color: '#fff', fontFamily: 'var(--font-data)' }}>
                  {safeIdx + 1}/{imageList.length}
                </span>
              </div>
            )}
          </div>

          {/* Thumbnail strip */}
          {imageList.length > 1 && (
            <div className="flex gap-1.5 px-2.5 py-2 overflow-x-auto" style={{ backgroundColor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
              {imageList.slice(0, 5).map((img, i) => (
                <button
                  key={`thumb-${i}-${img?.id}`}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  className="flex-shrink-0 rounded-md overflow-hidden transition-all duration-150 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-violet-500"
                  style={{
                    width: '32px',
                    height: '32px',
                    border: `2px solid ${i === safeIdx ? 'var(--color-primary)' : '#e5e5e5'}`,
                    borderRadius: '6px',
                    opacity: i === safeIdx ? 1 : 0.7,
                    transform: i === safeIdx ? 'scale(1.08)' : 'scale(1)',
                  }}
                  aria-label={`Ver imagen ${i + 1}`}
                  aria-pressed={i === safeIdx}
                >
                  <Image src={img?.url} alt={img?.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {/* Product info */}
          <div className="p-3" style={{ backgroundColor: '#fff' }}>
            {nombre ? (
              <h4
                className="font-bold line-clamp-2 mb-1"
                style={{ fontFamily: 'var(--font-heading)', color: '#1a1a1a', fontSize: '0.8rem', lineHeight: '1.3' }}
              >
                {nombre}
              </h4>
            ) : (
              <div className="h-3.5 rounded-md mb-1 w-3/4" style={{ backgroundColor: '#ebebeb' }} />
            )}

            {descripcion ? (
              <p
                className="line-clamp-2 mb-2.5"
                style={{ color: '#888', fontFamily: 'var(--font-body)', fontSize: '0.68rem', lineHeight: '1.4' }}
              >
                {descripcion}
              </p>
            ) : (
              <div className="space-y-1 mb-2.5">
                <div className="h-2.5 rounded w-full" style={{ backgroundColor: '#ebebeb' }} />
                <div className="h-2.5 rounded w-2/3" style={{ backgroundColor: '#ebebeb' }} />
              </div>
            )}

            <div className="flex items-center justify-between">
              {formattedPrice ? (
                <span
                  className="font-bold"
                  style={{ fontFamily: 'var(--font-data)', color: 'var(--color-primary)', fontSize: '0.95rem' }}
                >
                  {formattedPrice}
                </span>
              ) : (
                <div className="h-4 rounded w-14" style={{ backgroundColor: '#ebebeb' }} />
              )}
              <button
                className="px-2.5 py-1 rounded-lg text-white font-semibold"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  fontFamily: 'var(--font-caption)',
                  fontSize: '0.65rem',
                }}
                tabIndex={-1}
                aria-hidden="true"
              >
                Agregar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tip */}
      <div
        className="mt-4 flex items-start gap-2 rounded-xl border p-3"
        style={{ backgroundColor: 'rgba(255,255,255,0.50)', borderColor: 'rgba(17,24,39,0.08)' }}
      >
        <Icon name="Sparkles" size={13} color="var(--color-foreground)" className="flex-shrink-0 mt-0.5" />
        <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', lineHeight: '1.5' }}>
          {isRestaurant
            ? 'Los platos con imagen, descripción clara y precio visible reciben más pedidos.'
            : 'Productos con imagen y descripción completa reciben hasta 3× más pedidos.'}
        </p>
      </div>
    </div>
  );
}
